import { useQuery } from '@tanstack/react-query';
import { Ban, Smartphone, UserCheck, Users, Zap } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { useI18n, type Locale, type TranslationKey } from '../lib/i18n';
import { formatDateTime, relativeTime } from '../lib/format';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  type BadgeTone,
} from '../components/ui';

/**
 * The employee mobile app's activity log (operator, 2026-10-10: "i need full
 * log on mobile app activities reports in admin only for administrator and
 * filter by user"). The API writes a row for every request a phone makes —
 * refusals included — and the app's own sign-ins, so this page reads, never
 * writes. Administrators only (`mobile.activity.view`).
 */

type Outcome = 'SUCCESS' | 'REFUSED' | 'FAILED';

interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  employeeCode: string | null;
  jobTitle: string | null;
}

interface ActivityRow {
  id: string;
  userId: string | null;
  email: string | null;
  action: string;
  outcome: Outcome;
  entityType: string | null;
  entityId: string | null;
  label: string | null;
  method: string | null;
  path: string | null;
  statusCode: number | null;
  errorCode: string | null;
  durationMs: number | null;
  ipAddress: string | null;
  userAgent: string | null;
  platform: string | null;
  appVersion: string | null;
  metadata: Record<string, any> | null;
  occurredAt: string;
  user: Person | null;
  device: { id: string; platform: string; model: string | null; osVersion: string | null; appVersion: string | null } | null;
}

interface PersonRow {
  user: Person & { isActive: boolean };
  activities: number;
  lastActivityAt: string | null;
  phonesSignedIn: number;
}

interface PhoneRow {
  id: string;
  platform: string;
  model: string | null;
  osVersion: string | null;
  appVersion: string | null;
  signedInAt: string;
  signedOutAt: string | null;
  lastSeenAt: string;
  user: Person;
  _count: { activities: number };
}

/** The log's areas, in the app's own order — the part of an action before the dot. */
const AREAS = [
  'auth',
  'app',
  'dashboard',
  'notification',
  'mail',
  'chat',
  'ticket',
  'task',
  'leader_task',
  'announcement',
  'client_cr',
  'schedule',
  'leave',
  'payslip',
  'penalty',
  'wallet',
  'overtime',
  'remote_work',
  'meeting',
  'call',
  'support',
  'file',
  'attachment',
  'settings',
  'screen',
  'request',
] as const;

const OUTCOME_TONE: Record<Outcome, BadgeTone> = { SUCCESS: 'success', REFUSED: 'error', FAILED: 'warning' };

export function MobileActivityPage() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'activity';

  const { data: summary } = useQuery({
    queryKey: ['mobile-activity', 'summary'],
    queryFn: () =>
      api.get<{ today: string; actions: number; refused: number; activePeople: number; peopleSignedIn: number }>(
        '/mobile-activity/summary',
      ),
    refetchInterval: 60_000,
  });

  const setTab = (next: string) => {
    const copy = new URLSearchParams(params);
    copy.set('tab', next);
    setParams(copy, { replace: true });
  };

  return (
    <>
      <PageHeader title={t('mobile.title')} subtitle={t('mobile.subtitle')} />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric label={t('mobile.metricActions')} value={summary?.actions ?? '—'} icon={<Zap className="h-4 w-4" />} />
        <Metric
          label={t('mobile.metricPeople')}
          value={summary?.activePeople ?? '—'}
          icon={<Users className="h-4 w-4" />}
        />
        <Metric
          label={t('mobile.metricRefused')}
          value={summary?.refused ?? '—'}
          tone={summary?.refused ? 'error' : 'neutral'}
          icon={<Ban className="h-4 w-4" />}
          onClick={() => {
            const copy = new URLSearchParams({ tab: 'activity', outcome: 'REFUSED' });
            if (summary?.today) {
              copy.set('from', summary.today);
              copy.set('to', summary.today);
            }
            setParams(copy);
          }}
        />
        <Metric
          label={t('mobile.metricSignedIn')}
          value={summary?.peopleSignedIn ?? '—'}
          icon={<UserCheck className="h-4 w-4" />}
          onClick={() => setTab('phones')}
        />
      </div>

      <div className="mb-4">
        <Tabs
          tabs={[
            { id: 'activity', label: t('mobile.tabActivity') },
            { id: 'people', label: t('mobile.tabPeople') },
            { id: 'phones', label: t('mobile.tabPhones') },
          ]}
          active={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'people' ? <PeopleTab /> : tab === 'phones' ? <PhonesTab /> : <ActivityTab />}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Activity                                                                    */
/* -------------------------------------------------------------------------- */

const FILTERS = ['userId', 'action', 'outcome', 'from', 'to', 'q'] as const;

function ActivityTab() {
  const { t, locale, localized } = useI18n();
  const [params, setParams] = useSearchParams();
  const [inspect, setInspect] = useState<ActivityRow | null>(null);
  const page = Number(params.get('page') ?? '1') || 1;
  const filters = Object.fromEntries(
    FILTERS.map((key) => [key, params.get(key) ?? '']).filter(([, value]) => value !== ''),
  ) as Record<string, string>;

  const setFilter = (key: (typeof FILTERS)[number] | 'page', value: string) => {
    const copy = new URLSearchParams(params);
    if (value) copy.set(key, value);
    else copy.delete(key);
    if (key !== 'page') copy.delete('page');
    setParams(copy, { replace: true });
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mobile-activity', 'list', page, filters],
    queryFn: () => api.get<Paginated<ActivityRow>>('/mobile-activity', { page, pageSize: 50, ...filters }),
  });
  const { data: people } = useQuery({
    queryKey: ['mobile-activity', 'people'],
    queryFn: () => api.get<PersonRow[]>('/mobile-activity/people'),
  });
  const peopleByName = useMemo(
    () => [...(people ?? [])].sort((a, b) => localized(a.user).localeCompare(localized(b.user), locale)),
    [people, localized, locale],
  );

  return (
    <>
      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <Select
            aria-label={t('mobile.person')}
            value={filters.userId ?? ''}
            onChange={(e) => setFilter('userId', e.target.value)}
            className="lg:col-span-2"
          >
            <option value="">{t('mobile.allPeople')}</option>
            {peopleByName.map((p) => (
              <option key={p.user.id} value={p.user.id}>
                {localized(p.user)}
                {p.user.employeeCode ? ` · ${p.user.employeeCode}` : ''}
              </option>
            ))}
          </Select>
          <Select aria-label={t('mobile.area')} value={filters.action ?? ''} onChange={(e) => setFilter('action', e.target.value)}>
            <option value="">{t('mobile.allAreas')}</option>
            {AREAS.map((area) => (
              <option key={area} value={area}>
                {t(`mobile.area.${area}` as TranslationKey)}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t('mobile.outcome')}
            value={filters.outcome ?? ''}
            onChange={(e) => setFilter('outcome', e.target.value)}
          >
            <option value="">{t('mobile.allOutcomes')}</option>
            {(['SUCCESS', 'REFUSED', 'FAILED'] as const).map((o) => (
              <option key={o} value={o}>
                {t(`mobile.outcome${o}` as TranslationKey)}
              </option>
            ))}
          </Select>
          <Input
            type="date"
            aria-label={t('common.from')}
            value={filters.from ?? ''}
            onChange={(e) => setFilter('from', e.target.value)}
          />
          <Input
            type="date"
            aria-label={t('common.to')}
            value={filters.to ?? ''}
            onChange={(e) => setFilter('to', e.target.value)}
          />
          <Input
            type="search"
            className="sm:col-span-2 lg:col-span-6"
            placeholder={t('mobile.searchPlaceholder')}
            defaultValue={filters.q ?? ''}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setFilter('q', (e.target as HTMLInputElement).value.trim());
            }}
            onBlur={(e) => {
              if (e.target.value.trim() !== (filters.q ?? '')) setFilter('q', e.target.value.trim());
            }}
          />
        </div>
        <p className="border-t hairline px-4 py-2 text-caption text-muted">{t('mobile.privacyNote')}</p>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<Smartphone className="h-8 w-8" />} hint={t('mobile.noActivity')} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('mobile.when')}</Th>
                  <Th>{t('mobile.person')}</Th>
                  <Th>{t('mobile.what')}</Th>
                  <Th>{t('mobile.phone')}</Th>
                  <Th>{t('mobile.result')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <Tr key={row.id} onClick={() => setInspect(row)}>
                    <Td className="whitespace-nowrap tabular text-muted">
                      <span title={formatDateTime(row.occurredAt, locale)}>{formatDateTime(row.occurredAt, locale)}</span>
                    </Td>
                    <Td>
                      <PersonCell row={row} />
                    </Td>
                    <Td className="min-w-0">
                      <ActivityWhat row={row} />
                    </Td>
                    <Td className="whitespace-nowrap text-caption text-muted">
                      <PhoneCell device={row.device} platform={row.platform} appVersion={row.appVersion} />
                    </Td>
                    <Td>
                      <OutcomeBadge row={row} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={data.page}
              pageCount={data.pageCount}
              total={data.total}
              onChange={(next) => setFilter('page', String(next))}
            />
          </>
        )}
      </Card>

      {/* The read-only log viewer — the audit log's sanctioned popup, for the same job. */}
      {inspect && (
        <Modal open onClose={() => setInspect(null)} title={actionText(inspect, t)} size="lg">
          <ActivityDetail row={inspect} />
        </Modal>
      )}
    </>
  );
}

function PersonCell({ row }: { row: ActivityRow }) {
  const { t, localized } = useI18n();
  if (!row.user) {
    return <span className="text-faint">{t('mobile.unknownPerson', { email: row.email ?? '—' })}</span>;
  }
  return (
    <div className="min-w-0">
      <div className="truncate text-body-sm text-[var(--text-primary)]">{localized(row.user)}</div>
      {row.user.jobTitle && <div className="truncate text-caption text-muted">{row.user.jobTitle}</div>}
    </div>
  );
}

function PhoneCell({
  device,
  platform,
  appVersion,
}: {
  device: ActivityRow['device'];
  platform: string | null;
  appVersion: string | null;
}) {
  const model = device?.model ?? null;
  const system = device?.platform ?? platform;
  const version = appVersion ?? device?.appVersion ?? null;
  if (!model && !system) return <span className="text-faint">—</span>;
  return (
    <div className="flex items-center gap-1.5">
      <Smartphone className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span dir="ltr" className="truncate">
        {[model ?? system, version ? `v${version}` : null].filter(Boolean).join(' · ')}
      </span>
    </div>
  );
}

function OutcomeBadge({ row }: { row: ActivityRow }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Badge tone={OUTCOME_TONE[row.outcome]}>{t(`mobile.outcome${row.outcome}` as TranslationKey)}</Badge>
      {row.errorCode && row.outcome !== 'SUCCESS' && (
        <span className="font-mono text-mono-sm text-faint" dir="ltr">
          {row.errorCode}
        </span>
      )}
    </div>
  );
}

/** The action in words, with the record it touched — linked when the admin has a page for it. */
function ActivityWhat({ row }: { row: ActivityRow }) {
  const { t, locale } = useI18n();
  const extra = actionDetails(row, t, locale);
  const link = recordLink(row);
  return (
    <div className="min-w-0">
      <div className="text-body-sm text-[var(--text-primary)]">{actionText(row, t)}</div>
      {row.label &&
        (link ? (
          <Link
            to={link}
            onClick={(e) => e.stopPropagation()}
            className="block truncate text-caption text-primary-600 dark:text-primary-400 hover:underline"
            dir="auto"
          >
            {row.label}
          </Link>
        ) : (
          <div className="truncate text-caption text-muted" dir="auto">
            {row.label}
          </div>
        ))}
      {extra && <div className="text-caption text-faint">{extra}</div>}
    </div>
  );
}

type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/** A key that may not be in the dictionary: its own text, or `fallback`. */
function maybe(t: Translate, key: string, fallback: string): string {
  const text = t(key as TranslationKey);
  return text === key ? fallback : text;
}

function actionText(row: ActivityRow, t: Translate): string {
  if (row.action === 'mail.send' && row.metadata?.kind === 'reply') return t('mobile.action.mail.reply');
  if (row.action === 'mail.send' && row.metadata?.kind === 'forward') return t('mobile.action.mail.forward');
  if (row.action === 'chat.send' && row.metadata?.sticker) return t('mobile.action.chat.sticker');
  if (row.action === 'chat.send' && row.metadata?.reply) return t('mobile.action.chat.reply');
  return maybe(t, `mobile.action.${row.action}`, row.action);
}

/** What else the row says, in a few words: whom a mail went to, a leave's days, a refusal's route. */
function actionDetails(row: ActivityRow, t: Translate, locale: Locale): ReactNode {
  const m = row.metadata ?? {};
  const parts: string[] = [];
  switch (row.action) {
    case 'auth.login_failed':
      if (row.errorCode === 'INVALID_CREDENTIALS') parts.push(t('mobile.wrongPassword'));
      if (row.errorCode === 'ACCOUNT_DISABLED') parts.push(t('mobile.accountDisabled'));
      break;
    case 'mail.send':
      if (typeof m.recipients === 'number') {
        parts.push(m.recipients === 1 ? t('mobile.toPerson') : t('mobile.toPeople', { count: m.recipients }));
      }
      if (m.files) parts.push(t('mobile.withFiles', { count: m.files }));
      break;
    case 'chat.send':
      // How many files went with it: of a chat the log keeps nothing else (2026-10-19).
      if (m.files) parts.push(t('mobile.withFiles', { count: m.files }));
      break;
    case 'chat.forward':
      if (typeof m.people === 'number' && m.people > 0) {
        parts.push(m.people === 1 ? t('mobile.toPerson') : t('mobile.toPeople', { count: m.people }));
      }
      // Into how many groups — never which (2026-10-22).
      if (typeof m.groups === 'number') {
        parts.push(m.groups === 1 ? t('mobile.toGroup') : t('mobile.toGroups', { count: m.groups }));
      }
      break;
    case 'chat.group.create':
    case 'chat.group.invite':
      // How many were invited: of a group the log keeps nothing else (2026-10-22).
      if (typeof m.people === 'number' && m.people > 0) {
        parts.push(m.people === 1 ? t('mobile.invitedPerson') : t('mobile.invitedPeople', { count: m.people }));
      }
      break;
    case 'attachment.open':
    case 'notification.open':
      if (row.entityType === 'ChatConversation') parts.push(t('mobile.inChat'));
      break;
    case 'ticket.reply':
      if (m.internal) parts.push(t('mobile.internalNote'));
      if (m.files) parts.push(t('mobile.withFiles', { count: m.files }));
      break;
    case 'task.comment':
    case 'leader_task.comment':
      if (m.files) parts.push(t('mobile.withFiles', { count: m.files }));
      break;
    case 'leave.request':
    case 'leave.view':
    // Remote work's days (2026-10-28) — never its reason, nor what a team leader or an administrator wrote.
    case 'remote_work.request':
    case 'remote_work.view':
    case 'remote_work.approve':
    case 'remote_work.reject':
    case 'remote_work.cancel':
      if (m.startDate) parts.push(m.endDate && m.endDate !== m.startDate ? `${m.startDate} → ${m.endDate}` : m.startDate);
      if (typeof m.days === 'number') parts.push(m.days === 1 ? t('mobile.oneDay') : t('mobile.days', { count: m.days }));
      break;
    case 'payslip.view':
      if (m.period) parts.push(m.period);
      break;
    case 'penalty.view':
    // The penalty an objection was sent about — never why (2026-10-22).
    case 'penalty.object':
      if (m.days !== null && m.days !== undefined) {
        parts.push(m.days === 1 ? t('mobile.oneDay') : t('mobile.days', { count: m.days }));
      }
      if (m.month) parts.push(m.month);
      break;
    case 'wallet.redeem':
      // What the points bought, and how many: never the balance left (2026-10-21).
      if (m.type) parts.push(maybe(t, `wallet.type.${m.type}`, m.type));
      if (typeof m.points === 'number') parts.push(m.points === 1 ? t('mobile.onePoint') : t('mobile.points', { count: m.points }));
      if (typeof m.bundles === 'number') parts.push(m.bundles === 1 ? t('mobile.oneBundle') : t('mobile.bundles', { count: m.bundles }));
      break;
    case 'overtime.view':
    case 'overtime.accept':
    case 'overtime.decline':
      // The day and its most — never its reason, nor what the employee wrote (2026-10-21).
      if (m.workDate) parts.push(m.workDate);
      if (typeof m.minutes === 'number') parts.push(t('mobile.upTo', { time: minutesText(m.minutes) }));
      break;
    case 'meeting.view':
    case 'meeting.accept':
    case 'meeting.decline':
      // When the meeting is — never why someone declined it (2026-10-21).
      if (typeof m.startsAt === 'string') parts.push(formatDateTime(m.startsAt, locale));
      break;
    case 'settings.language':
      if (m.locale) parts.push(m.locale === 'ar' ? t('mobile.languageAr') : t('mobile.languageEn'));
      break;
    case 'settings.theme':
      if (m.value) parts.push(maybe(t, `mobile.theme${capitalize(m.value)}`, m.value));
      break;
    case 'screen.view':
      if (m.screen) parts.push(maybe(t, `mobile.screen.${m.screen}`, m.screen));
      break;
    case 'request.refused':
      if (m.route) return <span className="font-mono text-mono-sm" dir="ltr">{m.route}</span>;
      break;
    default:
      if (typeof m.total === 'number') parts.push(t('mobile.total', { count: m.total }));
      if (m.filters?.page && m.filters.page !== '1') parts.push(t('mobile.page', { page: m.filters.page }));
  }
  return parts.length ? parts.join(' · ') : null;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** 2:00 — an overtime's most, as hours and minutes, in either language. */
function minutesText(minutes: number): string {
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

/** The admin's page for what the row touched; mail stays private, so never a link. */
function recordLink(row: ActivityRow): string | null {
  if (!row.entityId) return null;
  switch (row.entityType) {
    case 'Ticket':
      return `/tickets/${row.entityId}`;
    case 'Task':
      return row.action.startsWith('leader_task.') ? `/leader-tasks/${row.entityId}` : `/tasks/${row.entityId}`;
    case 'Announcement':
      return `/announcements/${row.entityId}`;
    case 'ClientChangeRequest':
      return `/client-crs/${row.entityId}`;
    case 'LeaveRequest':
      return `/leaves/${row.entityId}`;
    case 'Penalty':
      return `/violations/penalties/${row.entityId}`;
    case 'OvertimeRequest':
      return `/overtime/${row.entityId}`;
    case 'RemoteWorkRequest':
      return `/remote-work/${row.entityId}`;
    case 'Meeting':
      return `/meetings/${row.entityId}`;
    default:
      return null;
  }
}

function ActivityDetail({ row }: { row: ActivityRow }) {
  const { t, locale, localized } = useI18n();
  const items: Array<[string, ReactNode]> = [
    [t('mobile.when'), formatDateTime(row.occurredAt, locale)],
    [t('mobile.person'), row.user ? localized(row.user) : t('mobile.unknownPerson', { email: row.email ?? '—' })],
    [t('mobile.result'), <OutcomeBadge key="o" row={row} />],
    [
      t('mobile.phone'),
      <PhoneCell key="p" device={row.device} platform={row.platform} appVersion={row.appVersion} />,
    ],
    [
      t('mobile.request'),
      row.method || row.path ? (
        <span key="r" className="font-mono text-mono-sm" dir="ltr">
          {[row.method, row.path, row.statusCode].filter(Boolean).join(' ')}
        </span>
      ) : (
        '—'
      ),
    ],
    [t('mobile.duration'), row.durationMs !== null ? `${row.durationMs} ms` : '—'],
    ['IP', <span key="ip" dir="ltr">{row.ipAddress ?? '—'}</span>],
  ];
  const link = recordLink(row);
  return (
    <div>
      <dl className="mb-4 grid gap-3 sm:grid-cols-2">
        {items.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-caption uppercase text-[var(--text-tertiary)]">{label}</dt>
            <dd className="text-body-sm text-[var(--text-primary)] break-words">{value}</dd>
          </div>
        ))}
        {row.label && (
          <div className="min-w-0 sm:col-span-2">
            <dt className="text-caption uppercase text-[var(--text-tertiary)]">{t('mobile.what')}</dt>
            <dd className="text-body-sm" dir="auto">
              {link ? (
                <Link to={link} className="text-primary-600 dark:text-primary-400 hover:underline">
                  {row.label}
                </Link>
              ) : (
                row.label
              )}
            </dd>
          </div>
        )}
        {row.userAgent && (
          <div className="min-w-0 sm:col-span-2">
            <dt className="text-caption uppercase text-[var(--text-tertiary)]">User agent</dt>
            <dd className="truncate text-caption text-faint" dir="ltr">
              {row.userAgent}
            </dd>
          </div>
        )}
      </dl>
      {row.metadata?.reportedBy === 'app' && <Badge tone="info">{t('mobile.reportedByApp')}</Badge>}
      {row.metadata && Object.keys(row.metadata).length > 0 && (
        <div className="mt-3 rounded-md border border-[var(--line)] bg-[var(--surface-sunken)] p-3">
          <p className="mb-1.5 text-caption uppercase text-[var(--text-tertiary)]">{t('mobile.details')}</p>
          <pre className="overflow-x-auto font-mono text-mono-sm text-[var(--text-primary)]" dir="ltr">
            {JSON.stringify(row.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* People                                                                      */
/* -------------------------------------------------------------------------- */

function PeopleTab() {
  const { t, locale, localized } = useI18n();
  const [, setParams] = useSearchParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mobile-activity', 'people'],
    queryFn: () => api.get<PersonRow[]>('/mobile-activity/people'),
  });

  return (
    <Card padded={false}>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.length ? (
        <EmptyState icon={<Users className="h-8 w-8" />} hint={t('mobile.noPeople')} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('mobile.person')}</Th>
              <Th align="end">{t('mobile.activities')}</Th>
              <Th>{t('mobile.lastActivity')}</Th>
              <Th align="end">{t('mobile.phonesSignedIn')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <Tr key={row.user.id} onClick={() => setParams({ tab: 'activity', userId: row.user.id })}>
                <Td>
                  <div className="min-w-0">
                    <div className="truncate text-body-sm text-[var(--text-primary)]">{localized(row.user)}</div>
                    <div className="truncate text-caption text-muted">
                      {[row.user.jobTitle, row.user.employeeCode].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                </Td>
                <Td align="end" className="tabular">
                  {row.activities}
                </Td>
                <Td className="whitespace-nowrap text-caption text-muted">
                  {row.lastActivityAt ? (
                    <span title={formatDateTime(row.lastActivityAt, locale)}>{relativeTime(row.lastActivityAt, locale)}</span>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td align="end" className="tabular">
                  {row.phonesSignedIn}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Phones                                                                      */
/* -------------------------------------------------------------------------- */

function PhonesTab() {
  const { t, locale, localized } = useI18n();
  const [, setParams] = useSearchParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mobile-activity', 'devices'],
    queryFn: () => api.get<PhoneRow[]>('/mobile-activity/devices'),
  });

  return (
    <Card padded={false}>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.length ? (
        <EmptyState icon={<Smartphone className="h-8 w-8" />} hint={t('mobile.noPhones')} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('mobile.person')}</Th>
              <Th>{t('mobile.phone')}</Th>
              <Th>{t('mobile.appVersion')}</Th>
              <Th>{t('mobile.lastSeen')}</Th>
              <Th>{t('mobile.status')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <Tr key={row.id} onClick={() => setParams({ tab: 'activity', userId: row.user.id })}>
                <Td>
                  <div className="truncate text-body-sm text-[var(--text-primary)]">{localized(row.user)}</div>
                </Td>
                <Td className="text-caption text-muted">
                  <div dir="ltr" className="truncate">
                    {[row.model ?? row.platform, row.osVersion].filter(Boolean).join(' · ')}
                  </div>
                </Td>
                <Td className="whitespace-nowrap text-caption text-muted" dir="ltr">
                  {row.appVersion ? `v${row.appVersion}` : '—'}
                </Td>
                <Td className="whitespace-nowrap text-caption text-muted">
                  <span title={formatDateTime(row.lastSeenAt, locale)}>{relativeTime(row.lastSeenAt, locale)}</span>
                </Td>
                <Td>
                  {row.signedOutAt ? (
                    <Badge tone="neutral" title={formatDateTime(row.signedOutAt, locale)}>
                      {t('mobile.signedOut')}
                    </Badge>
                  ) : (
                    <Badge tone="success" dot title={formatDateTime(row.signedInAt, locale)}>
                      {t('mobile.signedIn')}
                    </Badge>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
